import axios, { type AxiosResponse } from "axios";
import React, { type ReactElement } from "react";
import { useLocation, useParams } from "react-router-dom";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useResourceVerifier from "../../../hooks/useResourceVerifier";
import { ErrorBoundary, ErrorCard, groupValidation, LoadingCard } from "../../errors";

interface GroupResource {
	id: string;
	name?: string;
	[key: string]: unknown;
}

interface GroupWithErrorBoundaryProps {
	children: ReactElement;
}

const GroupWithErrorBoundary = ({ children }: GroupWithErrorBoundaryProps) => {
	const { groupId = "" } = useParams<{ groupId: string }>();
	const location = useLocation();
	const authHeader = useAuthHeader();

	const { 
		isLoading, 
		resourceError: groupError, 
		resourceData: groupData 
	} = useResourceVerifier<GroupResource>({
		id: groupId,
		validateIdFn: groupValidation.validateGroupId,
		fetchResourceFn: () => axios.get(
			apiRoutes.getGroupById(groupId),
			{ headers: authHeader }
		),
		validateResponseFn: (response: AxiosResponse<GroupResource>) => Boolean(response && response.data && !(response.data as any).error),
		authHeader,
		invalidIdMsg: "Invalid group ID format",
		timeoutMsg: "Timeout. Group may not exist or has been removed."
	});
	
	// Detect if we have a valid state for rendering children
	const canRenderChildren = !isLoading && !groupError && groupData && 
		location.state && typeof location.state === "object";

	// If we don't have state but need it, construct a basic one for the child components
	const getStateForChildren = (): Record<string, unknown> => {
		if (!location.state && groupData) {
			return {
				name: groupData.name || `Group ${groupId}`,
				id: groupId
			};
		}
		return (location.state ?? {}) as Record<string, unknown>;
	};

	if (isLoading) {
		return (
			<LoadingCard
				title="Loading Group"
				message="Please wait while we load the group..."
				subMessage="If this takes more than a few seconds, the group may not exist."
			/>
		);
	}

	if (groupError) {
		return (
			<ErrorCard 
				title="Group Not Found"
				message={groupError.message}
				returnPath="/settings/groups"
				returnButtonText="Return to Groups"
			/>
		);
	}

	return (
		<ErrorBoundary parentPath="/settings/groups">
			{React.cloneElement(children, { 
				...(children?.props ?? {}),
				...(canRenderChildren ? {} : { state: getStateForChildren() })
			})}
		</ErrorBoundary>
	);
};

export default GroupWithErrorBoundary;
